import * as xlsx from 'xlsx';
import FileSaver from 'file-saver';
import { WorkBook } from 'xlsx';
import { TranslateService } from '@ngx-translate/core';
import { ColumnKey, ColumnMappingService } from '@/core/services/column-mapping.service';
import { ReportSettings } from '@/core/models/report-settings.interface';
import { Layer } from '@/core/models/layer';
import { CurrentUser } from '@/core/models/user';
import { ActivityDetailVm } from '@/core/models/activity';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { AngularTranslations } from '@/core/types/angular-primeng-api';
import { is12hFormat } from '@/core/models/time-format';

/**
 * Key:Value representation for a single row in the Excel sheet.
 * The key is the identifier of the column and the value is the data for the field.
 */
type ExcelTableRow = {
    [key: string /* ExcelColumnKey */]: string | number;
};

type ExcelColumnKey =
    | ColumnKey
    | 'activities_start_date'
    | 'activities_start_time'
    | 'activities_end_date'
    | 'activities_end_time';

export class ExcelExporter {
    public static readonly EXCEL_FILE_EXTENSION = '.xlsx';
    private isPro: boolean;
    private isAdminOrManager: boolean;

    constructor(
        private readonly translate: TranslateService,
        private readonly columnMappingService: ColumnMappingService,
        private readonly reportSettings: ReportSettings,
        private readonly layers: Layer[],
        currentUser: CurrentUser,
    ) {
        this.isPro = currentUser.isPro();
        this.isAdminOrManager = currentUser.isAdminOrManager();
    }

    private getLayerName(layerIndex: number): string {
        return this.layers.find((layer) => layer.index === layerIndex)?.name ?? '';
    }

    private async getTranslations(): Promise<AngularTranslations> {
        return await firstValueFrom(
            this.translate.get([
                'activities_user',
                'activities_note',
                'activities_date',
                'activities_start_date',
                'activities_end_date',
                'activities_time',
                'activities_start_time',
                'activities_end_time',
                'activities_duration',
                'activities_billed',
                'standard_yes',
                'standard_no',
            ]),
        );
    }

    private getColumns(): ExcelColumnKey[] {
        const columns: ExcelColumnKey[] = [
            'activities_user',
            'layer_1',
            'layer_2',
            'layer_3',
            'activities_note',
            'activities_start_date',
            'activities_start_time',
            'activities_end_date',
            'activities_end_time',
            'activities_date',
            'activities_time',
            'activities_duration',
            'activities_billed',
        ];

        const isVisible = (excelColumnKey: string): boolean => {
            try {
                if (excelColumnKey === 'activities_time' || excelColumnKey === 'activities_date') {
                    // filter original column codes for time and date, for excel the 4 new columns codes are used instead
                    return false;
                }

                // map excel column key to reportsettings column key
                let columnKey: ColumnKey;
                switch (excelColumnKey) {
                    case 'activities_start_date':
                    case 'activities_end_date':
                        columnKey = 'activities_date';
                        break;
                    case 'activities_start_time':
                    case 'activities_end_time':
                        columnKey = 'activities_time';
                        break;
                    default:
                        columnKey = excelColumnKey as ColumnKey;
                }

                return this.columnMappingService.isColumnVisible(
                    columnKey,
                    this.isPro,
                    this.isAdminOrManager,
                    this.reportSettings,
                    this.layers,
                );
            } catch (error) {
                console.error('Error in isColumnVisible:', error);
                return true;
            }
        };

        return columns.filter((key) => isVisible(key));
    }

    private getHeader(
        columns: ExcelColumnKey[],
        translations: AngularTranslations,
    ): { header: string[]; headerMap: ExcelTableRow } {
        const columnDefintions: ExcelTableRow = {
            activities_user: translations['activities_user'],
            layer_1: this.getLayerName(1),
            layer_2: this.getLayerName(2),
            layer_3: this.getLayerName(3),
            activities_note: translations['activities_note'],
            activities_start_date: translations['activities_start_date'],
            activities_start_time: translations['activities_start_time'],
            activities_end_date: translations['activities_end_date'],
            activities_end_time: translations['activities_end_time'],
            activities_date: translations['activities_date'],
            activities_time: translations['activities_time'],
            activities_duration: translations['activities_duration'],
            activities_billed: translations['activities_billed'],
        };

        const finalHeader: ExcelTableRow = {};
        for (const column of columns) {
            finalHeader[column] = columnDefintions[column];
        }

        return {
            header: columns.map((key) => finalHeader[key] as string),
            headerMap: finalHeader,
        };
    }

    private getTableData(
        columns: ExcelColumnKey[],
        translations: AngularTranslations,
        headerMap: ExcelTableRow,
        activities: ActivityDetailVm[],
    ): ExcelTableRow[] {
        const tableData = activities.map((activity: ActivityDetailVm) => {
            const row: ExcelTableRow = {};

            const [startTime, rawEndTime] = activity.time.split(' - ');
            const endTime = rawEndTime.replace(/\(\+\d+\)/, '');

            for (const column of columns) {
                const header = headerMap[column] as string;

                switch (column) {
                    case 'activities_user':
                        row[header] = activity.userName;
                        break;
                    case 'layer_1':
                        row[header] = activity.layer1;
                        break;
                    case 'layer_2':
                        row[header] = activity.layer2!;
                        break;
                    case 'layer_3':
                        row[header] = activity.layer3!;
                        break;
                    case 'activities_note':
                        row[header] = activity.note;
                        break;
                    case 'activities_duration':
                        let formatedDuration: string | number = activity.duration;
                        // Check the duration format
                        if (typeof (formatedDuration as unknown) === 'string') {
                            if (this.reportSettings.durationFormat === 'Decimal') {
                                // Format is decimal hours ("1,5")
                                // Important: float value needs to use dot as decimal separator
                                formatedDuration = parseFloat(formatedDuration.replace(',', '.'));
                            } else {
                                // type safety check for string operations
                                formatedDuration = formatedDuration.replace('h ', ':');
                                formatedDuration = formatedDuration.replace('m ', ':');
                                formatedDuration = formatedDuration.slice(0, formatedDuration.length - 1);
                            }
                            row[header] = formatedDuration;
                        }
                        break;
                    case 'activities_start_date':
                        const startDateFormatted = moment(activity.start).format(this.reportSettings.dateFormat);
                        row[header] = startDateFormatted;
                        break;
                    case 'activities_end_date':
                        const endDateFormatted = moment(activity.end).format(this.reportSettings.dateFormat);
                        row[header] = endDateFormatted;
                        break;
                    case 'activities_start_time':
                        row[header] = startTime;
                        break;
                    case 'activities_end_time':
                        row[header] = endTime;
                        break;
                    case 'activities_billed':
                        row[header] = activity.billed ? translations['standard_yes'] : translations['standard_no'];
                        break;
                    case 'activities_time':
                    // row[header] = activity.time;
                    case 'activities_date':
                    // row[header] = activity.date;
                    default:
                        console.error(`Unexpected excel column "${column}"`);
                }
            }
            return row;
        });
        return tableData;
    }

    public async export(activities: ActivityDetailVm[], fileName: string): Promise<void> {
        const translations = await this.getTranslations();

        // the columns (keys) to be displayed
        const columns = this.getColumns();

        const { header, headerMap } = this.getHeader(columns, translations);

        const tableData = this.getTableData(columns, translations, headerMap, activities);

        const workbook = this.createWorkbook(columns, header, tableData);

        this.saveWorkbook(workbook, fileName);
    }

    private calculateColumnWidths(headers: string[], tableData: ExcelTableRow[]): Record<string, number> {
        const columnWidths: Record<string, number> = {};

        // Initialize columnWidths with the length of the column headers
        headers.forEach((key) => {
            columnWidths[key] = key.length;
        });

        // Calculate the max length of each column. Takes into account the length of the column header and the length of the longest value in the column
        tableData.forEach((row) => {
            Object.keys(row).forEach((columnKey) => {
                const value = row[columnKey];
                if (value) {
                    const length = ('' + value).length; // Get the length of the string
                    // Update the columnWidths only if this length is greater than the current max
                    columnWidths[columnKey] = Math.max(columnWidths[columnKey], length);
                }
            });
        });

        return columnWidths;
    }

    private createWorkbook(columns: ExcelColumnKey[], header: string[], tableData: ExcelTableRow[]): WorkBook {
        const columnWidths = this.calculateColumnWidths(header, tableData);

        // Build wscols from calculated widths dynamically
        const wscols = Object.keys(columnWidths).map((key) => ({
            wch: columnWidths[key] + 4, // +4 for padding
        }));

        const worksheet = xlsx.utils.json_to_sheet(tableData, {
            header,
        });

        // Convert the time columns from text to real numeric time values so Excel formulas (e.g. SUM) work.
        // Note: This is done after the worksheet is created as we use json_to_sheet method.
        this.applyTimeFormats(worksheet, columns, tableData.length);

        worksheet['!cols'] = wscols;
        const workbook: WorkBook = {
            Sheets: { data: worksheet },
            SheetNames: ['data'],
        };

        return workbook;
    }

    /**
     * Replaces the textual time values of the time columns with real numeric Excel time values
     * (a fraction of a day) and assigns a matching number format. This allows the cells to be used
     * in Excel formulas such as SUM(). Decimal durations are already exported as numbers and are
     * left untouched.
     */
    private applyTimeFormats(worksheet: xlsx.WorkSheet, columns: ExcelColumnKey[], dataRowCount: number): void {
        // Excel time format depends on whether the user uses a 12h (AM/PM) or 24h time format.
        // Note: 12h format in Excel (and most apps) is h:mm and not hh:mm. Therefore h:mm is used here!
        const clockFormat = is12hFormat(this.reportSettings.timeFormat) ? 'h:mm AM/PM' : 'hh:mm';

        columns.forEach((column, columnIndex) => {
            const isClock = column === 'activities_start_time' || column === 'activities_end_time';
            const isDuration = column === 'activities_duration';
            if (!isClock && !isDuration) {
                return;
            }

            // start at 1 to skip the header row
            for (let row = 1; row <= dataRowCount; row++) {
                const address = xlsx.utils.encode_cell({ r: row, c: columnIndex });
                const cell = worksheet[address];
                // Only text cells are converted. Decimal durations are already numbers and stay as-is.
                if (!cell || typeof cell.v !== 'string' || cell.v.trim() === '') {
                    continue;
                }

                const raw = cell.v;
                const serial = isClock ? this.clockTimeToSerial(raw) : this.colonTimeToSerial(raw);
                if (serial === null) {
                    continue;
                }

                cell.t = 'n';
                cell.v = serial;
                // For durations use elapsed-hours brackets so totals beyond 24h are displayed correctly.
                cell.z = isClock ? clockFormat : raw.split(':').length > 2 ? '[h]:mm:ss' : '[h]:mm';
            }
        });
    }

    /**
     * Parses a clock time string (e.g. "08:30" or "8:30 AM") using the configured time format
     * and returns it as a fraction of a day, or null when it cannot be parsed.
     */
    private clockTimeToSerial(raw: string): number | null {
        const parsed = moment(raw.trim(), this.reportSettings.timeFormat, true);
        if (!parsed.isValid()) {
            return null;
        }
        return (parsed.hours() * 3600 + parsed.minutes() * 60 + parsed.seconds()) / 86400;
    }

    /**
     * Parses a colon separated duration string (e.g. "1:30" or "1:30:15") and returns it as a
     * fraction of a day, or null when it cannot be parsed.
     */
    private colonTimeToSerial(raw: string): number | null {
        const parts = raw.trim().split(':');
        if (parts.length < 2) {
            return null;
        }
        const numbers = parts.map((part) => Number(part));
        if (numbers.some((value) => Number.isNaN(value))) {
            return null;
        }
        const [hours, minutes, seconds = 0] = numbers;
        return (hours * 3600 + minutes * 60 + seconds) / 86400;
    }

    private saveWorkbook(workbook: WorkBook, fileName: string): void {
        const excelBuffer = xlsx.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
        });
        const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const data: Blob = new Blob([excelBuffer], {
            type: EXCEL_TYPE,
        });
        FileSaver.saveAs(data, fileName, { autoBom: false });
    }
}
